import { CircularProgress, makeStyles, Popover } from '@material-ui/core';
import { useEffect, useState, useRef } from 'react';
import foundersAPI, { Founder } from '../../api/founders';
import mentorsAPI, { Mentor } from '../../api/mentors';
import { VentureId } from '../../api/ventures/types/Venture';
import { Text } from '../common';

type BaseProps = {
  ventureId: string;
  children?: React.ReactNode;
};

type MentorProps = BaseProps & {
  type: 'mentors';
  initialAssigned?: Mentor[];
  onLoaded?: (mentors: Mentor[]) => any;
};

type FounderProps = BaseProps & {
  type: 'founders';
  initialAssigned?: Founder[];
  onLoaded?: (founders: Founder[]) => any;
};

type AssignedTableInfoProps = MentorProps | FounderProps;

const useStyles = makeStyles({
  popover: {
    pointerEvents: 'none',
  },
  paper: {
    width: 320,
    boxSizing: 'border-box',
  },
  loader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  wrapper: {
    minWidth: 20,
  },
  paragraph: {
    '& + &': {
      marginTop: 12,
    },
  },
});

function AssignedTableInfo({
  type,
  ventureId,
  initialAssigned,
  onLoaded = () => {},
  children,
}: AssignedTableInfoProps) {
  const classes = useStyles();
  const [assigned, setAssigned] = useState<Mentor[] | Founder[]>(
    initialAssigned || [],
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement>();
  const debouncedTimer = useRef<number>();

  const handleOpenInfo = async (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.target as HTMLElement);
    if (assigned.length === 0 && !isLoading) {
      try {
        setIsLoading(true);
        setIsOpen(true);

        const loadedAssigned =
          type === 'founders'
            ? await foundersAPI.getFoundersDetailsByVentureId(
                ventureId as VentureId,
              )
            : await mentorsAPI.getMentorsDetailsByVentureId(ventureId);

        setAssigned(loadedAssigned);
        onLoaded(loadedAssigned as Mentor[] & Founder[]);
        setIsLoading(false);
      } catch (e: any) {
        console.log('error handleOpenInfo', e);
      }
    }
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    debouncedTimer.current = setTimeout(() => {
      handleOpenInfo(event);
    }, 300) as unknown as number;
  };

  const handleMouseLeave = () => {
    clearTimeout(debouncedTimer.current);
    setIsOpen(false);
  };

  useEffect(() => {
    if (initialAssigned) {
      setAssigned(initialAssigned);
    }
  }, [initialAssigned]);

  return (
    <>
      <div
        className={classes.wrapper}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}>
        {children}
      </div>
      <Popover
        className={classes.popover}
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        elevation={2}
        PaperProps={{
          className: classes.paper,
        }}>
        {isLoading ? (
          <div className={classes.loader}>
            <CircularProgress size={24} color='primary' />
          </div>
        ) : (
          !!assigned.length && (
            <Text variant='normal2'>
              {assigned.map((data) => (
                <p key={data.id} className={classes.paragraph}>
                  {`${data.firstName} ${data.lastName}`}
                </p>
              ))}
            </Text>
          )
        )}
      </Popover>
    </>
  );
}

export default AssignedTableInfo;
